import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Wallet, Car, Star, TrendingUp, Power, Loader, AlertTriangle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import DriverLayout from '../../components/driver/DriverLayout';
import { showErrorToast } from '../../utils/errorHandler';

const StatCard = ({ title, value, icon: Icon, color }) => (
  <Card className="p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 mb-1">{title}</p>
        <p className="text-3xl font-bold text-gray-900">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
    </div>
  </Card>
);

const DriverDashboard = () => {
  const navigate = useNavigate();
  const [driverProfile, setDriverProfile] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    fetchDriverData();
  }, []);

  const fetchDriverData = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      
      // Fetch driver profile
      const profileResponse = await api.get('/drivers/my-profile');
      
      if (!profileResponse.data.driver) {
        // No driver profile, redirect to application
        navigate('/driver/apply');
        return;
      }
      
      setDriverProfile(profileResponse.data.driver);
      
      // Fetch earnings (only if we have a driver profile)
      try {
        const earningsResponse = await api.get('/drivers/earnings');
        setEarnings(earningsResponse.data.earnings);
      } catch (earningsError) {
        console.error('Error fetching earnings:', earningsError);
        // Set default earnings if fetch fails
        setEarnings({
          totalEarnings: 0,
          todayEarnings: 0,
          weekEarnings: 0,
          monthEarnings: 0,
          totalTrips: 0,
          walletBalance: profileResponse.data.driver?.walletBalance || 0
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching driver data:', error);
      console.error('Error details:', error.response);
      showErrorToast(toast, error, 'Failed to load dashboard');
      setLoading(false);
    }
  };

  const handleToggleOnline = async () => {
    setToggling(true);
    
    try {
      const api = (await import('../../utils/api')).default;
      const newStatus = !driverProfile.isOnline;
      
      await api.put('/drivers/status', { isOnline: newStatus });
      
      setDriverProfile({ ...driverProfile, isOnline: newStatus });
      
      toast({
        title: newStatus ? 'You are now online' : 'You are now offline',
        description: newStatus ? 'You will receive ride requests' : 'You will not receive ride requests',
      });
    } catch (error) {
      console.error('Error toggling status:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update status',
        variant: 'destructive',
      });
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <DriverLayout>
        <div className="flex items-center justify-center h-96">
          <Loader className="h-12 w-12 animate-spin text-pink-600" />
        </div>
      </DriverLayout>
    );
  }

  // If no driver profile exists, show application prompt
  if (!driverProfile) {
    return (
      <DriverLayout>
        <Card className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
            <Car className="h-8 w-8 text-pink-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Become a Driver</h2>
          <p className="text-gray-600 mb-6">
            Start earning by driving with Tekstar. Apply now to get started!
          </p>
          <Button
            onClick={() => navigate('/driver/apply')}
            className="bg-pink-600 hover:bg-pink-700"
          >
            Apply to Drive
          </Button>
        </Card>
      </DriverLayout>
    );
  }

  // Check approval status
  if (driverProfile.approvalStatus === 'pending') {
    return (
      <DriverLayout>
        <Card className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
            <Loader className="h-8 w-8 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Under Review</h2>
          <p className="text-gray-600 mb-4">
            Your driver application is currently being reviewed by our team.
          </p>
          <p className="text-sm text-gray-500">
            You will be notified once your application is approved.
          </p>
        </Card>
      </DriverLayout>
    );
  }

  if (driverProfile.approvalStatus === 'rejected') {
    return (
      <DriverLayout>
        <Card className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <span className="text-2xl">❌</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Rejected</h2>
          <p className="text-gray-600">
            Unfortunately, your driver application was not approved at this time.
          </p>
        </Card>
      </DriverLayout>
    );
  }

  if (driverProfile.approvalStatus === 'suspended') {
    return (
      <DriverLayout>
        <Card className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Suspended</h2>
          <p className="text-gray-600 mb-4">
            Your driver account has been suspended. This may be due to insufficient wallet balance.
          </p>
          <Button
            onClick={() => navigate('/driver/wallet')}
            className="bg-pink-600 hover:bg-pink-700"
          >
            Deposit Funds to Continue
          </Button>
        </Card>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="space-y-6">
        {/* Header with Online/Offline Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome back, {driverProfile.name}!</p>
          </div>
          <Button
            onClick={handleToggleOnline}
            disabled={toggling || driverProfile.walletBalance <= 0}
            className={`flex items-center gap-2 ${
              driverProfile.isOnline
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-gray-600 hover:bg-gray-700'
            }`}
          >
            <Power className="h-4 w-4" />
            {toggling ? 'Updating...' : (driverProfile.isOnline ? 'Go Offline' : 'Go Online')}
          </Button>
        </div>

        {/* Status Badge */}
        <div className="flex items-center gap-2">
          <Badge className={driverProfile.isOnline ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
            {driverProfile.isOnline ? '🟢 Online' : '⚫ Offline'}
          </Badge>
          {driverProfile.walletBalance <= 0 && (
            <Badge className="bg-red-100 text-red-800">
              ⚠️ Low Balance - Deposit Required
            </Badge>
          )}
        </div>

        {/* Zero Balance Alert */}
        {driverProfile.walletBalance <= 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="zero-balance-alert">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">Wallet Balance is Zero</h3>
              <p className="text-sm text-red-700 mt-1">You cannot go online or accept rides until you top up your wallet. Commission (20%) is deducted from your balance for each completed ride.</p>
              <Button
                onClick={() => navigate('/driver/wallet')}
                className="mt-3 bg-red-600 hover:bg-red-700 text-white"
                size="sm"
                data-testid="topup-wallet-btn"
              >
                <Wallet className="h-4 w-4 mr-2" />
                Top Up Wallet Now
              </Button>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Wallet Balance"
            value={`K${earnings?.walletBalance?.toFixed(2) || '0.00'}`}
            icon={Wallet}
            color="bg-gradient-to-br from-green-500 to-green-600"
          />
          <StatCard
            title="Completed Rides"
            value={earnings?.completedRides || 0}
            icon={Car}
            color="bg-gradient-to-br from-blue-500 to-blue-600"
          />
          <StatCard
            title="Rating"
            value={earnings?.rating?.toFixed(1) || '5.0'}
            icon={Star}
            color="bg-gradient-to-br from-yellow-500 to-yellow-600"
          />
          <StatCard
            title="Today's Earnings"
            value={`K${earnings?.todayEarnings?.toFixed(2) || '0.00'}`}
            icon={TrendingUp}
            color="bg-gradient-to-br from-purple-500 to-purple-600"
          />
        </div>

        {/* Earnings Details */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Earnings Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Today's Rides</p>
              <p className="text-2xl font-bold text-gray-900">{earnings?.todayRides || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Today's Commission</p>
              <p className="text-2xl font-bold text-red-600">-K{earnings?.todayCommission?.toFixed(2) || '0.00'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Earnings</p>
              <p className="text-2xl font-bold text-gray-900">K{earnings?.totalEarnings?.toFixed(2) || '0.00'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Commission Paid</p>
              <p className="text-2xl font-bold text-red-600">-K{earnings?.totalCommissionPaid?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button
            onClick={() => navigate('/driver/rides')}
            variant="outline"
            className="h-24 flex-col gap-2"
          >
            <Car className="h-6 w-6" />
            <span>Available Rides</span>
          </Button>
          <Button
            onClick={() => navigate('/driver/wallet')}
            variant="outline"
            className="h-24 flex-col gap-2"
          >
            <Wallet className="h-6 w-6" />
            <span>Manage Wallet</span>
          </Button>
          <Button
            onClick={() => navigate('/driver/history')}
            variant="outline"
            className="h-24 flex-col gap-2"
          >
            <Car className="h-6 w-6" />
            <span>My Rides</span>
          </Button>
        </div>

        {/* Commission Info */}
        <Card className="p-6 bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">💡 Commission Information</h3>
          <p className="text-sm text-blue-800">
            Commission rate: <strong>20% of ride price</strong>
          </p>
          <p className="text-xs text-blue-600 mt-2">
            Commission is automatically deducted from your wallet when you complete a ride.
            Keep your wallet topped up to continue driving!
          </p>
        </Card>
      </div>
    </DriverLayout>
  );
};

export default DriverDashboard;
